<template>
  <div class="relative">
    <!-- Label -->
    <span v-if="label" class="block mb-1 text-sm font-medium text-trader-text">{{ label }}</span>

    <!-- Search input mit elegantem Design -->
    <div class="search-container relative mb-1">
      <input
          ref="inputEl"
          v-model="searchTerm"
          @click="handleInputClick"
          @input="filterItems"
          type="text"
          :placeholder="placeholder"
          class="w-full py-1.5 px-2 border border-gray-300 text-trader-text rounded-md shadow-sm focus:ring-1 focus:ring-blue-400 focus:border-blue-400 outline-none transition-all duration-200"
      />
      <div class="absolute right-2 top-2 text-trader-text pointer-events-none">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-4 h-4">
          <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clip-rule="evenodd" />
        </svg>
      </div>
    </div>

    <!-- Dropdown mit nur Namen -->
    <div class="custom-select relative">
      <div
          v-if="isOpen"
          class="options-container fixed mt-1 border border-gray-200 rounded-md bg-white dark:bg-trader-gray max-h-48 overflow-y-auto z-[9999] shadow-lg"
          :style="dropdownStyle"
      >
        <div
            v-for="(item, index) in filteredItems"
            :key="index"
            @click="selectItem(item)"
            :class="[
              'option py-1.5 px-2.5 flex items-center hover:bg-trader-blue cursor-pointer text-sm',
              index !== filteredItems.length - 1 ? 'border-b border-gray-100' : ''
            ]"
        >
          <div class="flex flex-col">
            <div class="flex items-center">
              <!-- Nur den Namen anzeigen -->
              <span class="text-trader-text">{{ item[nameField] || '' }}</span>
            </div>
            <span v-if="item.description" class="text-trader-text text-xs mt-0.5">{{ item.description }}</span>
          </div>
        </div>
        <div v-if="filteredItems.length === 0" class="p-2 text-trader-text text-center text-sm">
          Keine Ergebnisse gefunden
        </div>
      </div>
    </div>

    <!-- Ausgewählter Wert (optional) - ohne ID -->
    <div v-if="selectedItem && showSelected" class="mt-1.5 py-1 px-2 bg-blue-50 border border-blue-100 rounded-md text-sm text-gray-700">
      <span class="text-trader-text mr-1">Ausgewählt:</span>
      <span class="font-medium">
        {{ selectedItem[nameField] || '' }}
      </span>
    </div>

    <!-- Keine Auswahl Indikator -->
    <div v-else-if="showNoSelection" class="mt-1.5 py-1 px-2 bg-gray-50 border border-gray-200 rounded-md text-sm text-trader-text">
      Keine Auswahl getroffen
    </div>
  </div>
</template>

<script>
export default {
  name: 'TRSearchableSelect',
  props: {
    // Array of items to display in the dropdown
    items: {
      type: Array,
      required: true,
      default: () => []
    },
    // The label to display above the input
    label: {
      type: String,
      default: ''
    },
    // The placeholder text for the search input
    placeholder: {
      type: String,
      default: 'Suchen...'
    },
    // The name of the ID field in the items array
    idField: {
      type: String,
      default: 'id'
    },
    // The name of the display field in the items array
    nameField: {
      type: String,
      default: 'name'
    },
    // Whether to show the selected item below the dropdown
    showSelected: {
      type: Boolean,
      default: false
    },
    // Whether to show the "no selection" message
    showNoSelection: {
      type: Boolean,
      default: false
    },
    // The initially selected value (ID)
    modelValue: {
      type: [String, Number, null],
      default: null
    },
    // Whether the control is disabled
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      searchTerm: '',
      isOpen: false,
      filteredItems: [],
      localValue: this.modelValue,
      dropdownStyle: {
        left: '0px',
        top: '0px',
        width: '0px'
      },
      clickOutsideHandler: null,
      isProcessingSelection: false
    };
  },
  computed: {
    selectedItem() {
      if (!this.localValue) return null;

      // Schutz vor undefinierten Items
      if (!this.items || !this.items.length) return null;

      return this.items.find(item =>
          item && item[this.idField] !== undefined &&
          String(item[this.idField]) === String(this.localValue)
      );
    }
  },
  watch: {
    items: {
      handler(newItems) {
        this.filterItems();
      },
      deep: true
    },
    modelValue: {
      handler(newValue) {
        // Verhindern von Endlosschleifen
        if (this.isProcessingSelection) return;

        this.localValue = newValue;

        // Nur aktualisieren, wenn es einen ausgewählten Wert gibt
        if (newValue !== null && newValue !== undefined) {
          this.$nextTick(() => {
            const selectedItem = this.selectedItem;
            if (selectedItem && selectedItem[this.nameField]) {
              this.searchTerm = selectedItem[this.nameField];
            }
          });
        }
      },
      immediate: true
    }
  },
  mounted() {
    this.filterItems();

    // Create a bound version of the event handler
    this.clickOutsideHandler = this.handleClickOutside.bind(this);

    // Close dropdown when clicking outside
    document.addEventListener('click', this.clickOutsideHandler);

    // Handle window resize for dropdown positioning
    window.addEventListener('resize', this.updateDropdownPosition);
    window.addEventListener('scroll', this.updateDropdownPosition, true);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.clickOutsideHandler);
    window.removeEventListener('resize', this.updateDropdownPosition);
    window.removeEventListener('scroll', this.updateDropdownPosition, true);
  },
  methods: {
    filterItems() {
      // Schutz vor undefinierten Items
      if (!this.items || !Array.isArray(this.items)) {
        this.filteredItems = [];
        return;
      }

      if (!this.searchTerm) {
        this.filteredItems = [...this.items];
        return;
      }

      const term = this.searchTerm.toLowerCase();
      this.filteredItems = this.items.filter(item => {
        if (!item) return false;

        const idValue = item[this.idField];
        const nameValue = item[this.nameField];
        const descValue = item.description;

        const idMatch = idValue !== undefined ? String(idValue).toLowerCase().includes(term) : false;
        const nameMatch = nameValue !== undefined ? String(nameValue).toLowerCase().includes(term) : false;
        const descMatch = descValue !== undefined ? String(descValue).toLowerCase().includes(term) : false;

        return idMatch || nameMatch || descMatch;
      });
    },
    selectItem(item) {
      if (!item) return;

      try {
        this.isProcessingSelection = true;
        this.localValue = item[this.idField];
        this.searchTerm = item[this.nameField] || '';
        this.isOpen = false;

        // Emit the selected value to the parent
        this.$emit('update:modelValue', item[this.idField]);
        this.$emit('change', item[this.idField]);
      } finally {
        // Immer zurücksetzen, auch wenn Fehler auftreten
        this.isProcessingSelection = false;
      }
    },
    handleClickOutside(event) {
      // Skip if the dropdown is already closed
      if (!this.isOpen) return;

      // Check if the click is outside this component
      if (this.$el && !this.$el.contains(event.target)) {
        this.isOpen = false;
      }
    },
    handleInputClick(event) {
      if (this.disabled) return;

      // Prevent the click from being processed by the document click handler
      event.stopPropagation();

      // Toggle dropdown state
      this.toggleDropdown();
    },
    toggleDropdown() {
      if (this.disabled) return;

      this.isOpen = !this.isOpen;

      if (this.isOpen) {
        this.$nextTick(() => {
          this.updateDropdownPosition();
        });
      }
    },
    updateDropdownPosition() {
      const inputEl = this.$refs.inputEl;
      if (!inputEl) return;

      const rect = inputEl.getBoundingClientRect();
      this.dropdownStyle = {
        left: rect.left + 'px',
        top: (rect.top + rect.height + 4) + 'px', // Added a small gap
        width: rect.width + 'px'
      };
    },
    reset() {
      this.isProcessingSelection = true;
      try {
        this.localValue = null;
        this.searchTerm = '';
        this.$emit('update:modelValue', null);
      } finally {
        this.isProcessingSelection = false;
      }
    }
  }
};
</script>

<style scoped>
.custom-select {
  user-select: none;
}

.options-container {
  scrollbar-width: thin;
  scrollbar-color: #cbd5e0 #f8fafc;
}

.options-container::-webkit-scrollbar {
  width: 6px;
}

.options-container::-webkit-scrollbar-track {
  background: #f8fafc;
}

.options-container::-webkit-scrollbar-thumb {
  background-color: #cbd5e0;
  border-radius: 20px;
}

input:disabled {
  background-color: #f3f4f6;
  cursor: not-allowed;
}
</style>